import React, { useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../../utils/apiBase';

interface WhatsAppStatus {
  conectado: boolean;
  numeroConfigurado: string | null;
  qrCode?: string | null;
}

export const WhatsAppManager: React.FC = () => {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(apiUrl('/whatsapp/status'));
      const data = await res.json();
      if (data?.whatsapp) {
        setStatus({
          conectado: data.whatsapp.conectado || data.whatsapp.isReady,
          numeroConfigurado: data.whatsapp.numeroConfigurado,
          qrCode: data.whatsapp.qrCode
        });
      }
    } catch (e) {
      console.error('Erro ao buscar status WhatsApp:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const gerarQrCode = async () => {
    setGenerating(true);
    try {
      await fetch(apiUrl('/whatsapp/inicializar'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: 'default' })
      });
      // Aguarda e atualiza status
      setTimeout(fetchStatus, 2000);
    } catch (e) {
      console.error('Erro ao gerar QR:', e);
    } finally {
      setGenerating(false);
    }
  };

  const forcarReset = async () => {
    const wipe = window.confirm(
      'Reset forçado da sessão WhatsApp.\n\n' +
      'OK = Limpar TUDO (apaga sessão local, vai pedir QR Code novo).\n' +
      'Cancelar = Tentar reset suave primeiro (só mata Chromium travado).'
    );
    setResetting(true);
    try {
      const res = await fetch(apiUrl('/whatsapp/force-reset'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: 'default', wipe })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert('Falha no reset: ' + (data?.message || res.status));
      }
      setTimeout(fetchStatus, 2500);
    } catch (e) {
      console.error('Erro no force-reset:', e);
      alert('Erro de rede ao forçar reset.');
    } finally {
      setResetting(false);
    }
  };

  const desconectar = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar a sessão do WhatsApp? Será necessário escanear o QR Code novamente para reconectar.')) {
      return;
    }
    setDisconnecting(true);
    try {
      const res = await fetch(apiUrl('/whatsapp/desconectar'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: 'default' })
      });
      if (!res.ok) {
        console.error('Erro ao desconectar WhatsApp:', res.status);
      }
      // Atualiza status após desconectar
      setTimeout(fetchStatus, 1500);
    } catch (e) {
      console.error('Erro ao desconectar WhatsApp:', e);
    } finally {
      setDisconnecting(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 3000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-4 max-w-xs">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
          <div className="h-4 bg-gray-200 rounded w-24"></div>
        </div>
      </div>
    );
  }

  const isConnected = status?.conectado;
  const qrCode = status?.qrCode;

  return (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden max-w-xs">
      {/* Header */}
      <div className={`p-3 ${isConnected ? 'bg-green-500' : 'bg-gray-500'} text-white`}>
        <div className="flex items-center gap-2">
          <span className="text-lg">📱</span>
          <div>
            <h2 className="font-bold text-sm">WhatsApp Notificações</h2>
            <p className="text-xs opacity-90">
              {isConnected ? '✓ Sessão ativa' : '✗ Desconectado'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {isConnected ? (
          /* CONECTADO */
          <div className="text-center py-2">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-2xl">✅</span>
            </div>
            <p className="text-green-700 font-medium text-sm">Sessão ativa</p>
            <p className="text-gray-500 text-xs">Pronto para enviar notificações</p>
            {status?.numeroConfigurado && (
              <p className="text-gray-400 text-xs mt-1">{status.numeroConfigurado}</p>
            )}
            <button
              onClick={desconectar}
              disabled={disconnecting}
              className="mt-3 px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded text-xs font-medium hover:bg-red-100 disabled:opacity-50"
            >
              {disconnecting ? '⏳ Desconectando...' : '🔌 Desconectar sessão'}
            </button>
          </div>
        ) : qrCode ? (
          /* QR CODE */
          <div className="text-center">
            <img 
              src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
              alt="QR Code"
              className="w-48 h-48 mx-auto border rounded"
            />
            <p className="text-xs text-gray-600 mt-2">
              Escaneie com o WhatsApp do celular
            </p>
            <p className="text-xs text-gray-400">
              Menu ⋮ → Aparelhos conectados → Conectar
            </p>
            <button
              onClick={gerarQrCode}
              disabled={generating}
              className="mt-3 px-3 py-1.5 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 disabled:opacity-50"
            >
              {generating ? '⏳ Gerando...' : '🔄 Gerar novo QR'}
            </button>
            <button
              onClick={forcarReset}
              disabled={resetting}
              className="ml-2 mt-3 px-3 py-1.5 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded text-xs hover:bg-yellow-100 disabled:opacity-50"
              title="Mata o Chromium travado e reinicia a sessão"
            >
              {resetting ? '⏳ Resetando...' : '🛠️ Forçar reset'}
            </button>
          </div>
        ) : (
          /* BOTÃO GERAR */
          <div className="text-center py-4">
            <p className="text-gray-500 text-sm mb-3">Clique para conectar</p>
            <button
              onClick={gerarQrCode}
              disabled={generating}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {generating ? '⏳ Gerando...' : '📱 Gerar QR Code'}
            </button>
            <div className="mt-3">
              <button
                onClick={forcarReset}
                disabled={resetting}
                className="text-xs text-yellow-700 underline hover:text-yellow-800 disabled:opacity-50"
                title="Use se o QR Code não aparecer após clicar em Gerar"
              >
                {resetting ? '⏳ Resetando...' : '🛠️ Não funciona? Forçar reset'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppManager;
